import React, { useState } from 'react';
import { Badge } from 'shared-components/components';
import { CardTitle, CardBody, CardBodyGroup, CardText, BubbleTitle } from './styles';
import CONSTANTS from './constants';
import { Tab, Box, Typography, useTheme, Divider, Stack, Grid, Button, Link } from '@mui/material';
import TabContext from '@mui/lab/TabContext';
import TabList from '@mui/lab/TabList';
import TabPanel from '@mui/lab/TabPanel';
import dayjs from 'dayjs';
import { CurrentAppConfig } from 'op-pages/RO/Careplan/AppConfig';

interface Props {
  name: string;
  patientDiagnosis: {
    id: string;
    isPrimaryDiagnosis: boolean;
    diagnosisCode: {
      diagnosisCode: string;
      id: string;
      diagnosisDescription: string;
      tumourStream: {
        name: string;
      };
    };
  }[];
}

const breastIntakeFieldNameMap = {
  grade: 'Grade',
  er: 'ER',
  pgr: 'PgR',
  her2: 'Her2',
  mastectomy: 'Mastectomy',
  reconstruction: 'Reconstruction',
  surgery: 'Surgery',
  oncoplasticSurgery: 'Oncoplastic Surgery',
  reconstructionTimeline: 'Reconstruction timeline',
  reconstructionType: 'Reconstruction type',
  expandersInSitu: 'Expanders In Situ',
  lymphovascularInvasion: 'Lymphovascular Invasion',
  lymphovascularInvasionExtent: 'Extent of LVI',
  radialMarginStatus: 'Radial margin status',
  neoadjuvantSystemicTherapy: 'Neoadjuvant Systemic Therapy',
  nstAdditionalInfo: 'NST additional info',
  exerciseDiscussedWithPatient: 'Exercise discussed with patient',
  excerciseReferral: 'Referred for exercise',
};

const lungIntakeFieldNameMap = {
  weightLoss: 'Weight loss',
  smokingHistory: 'Smoking history',
  egfr: 'EGFR',
  alk: 'ALK',
  pdl1: 'PDL1',
};

const urogenitalIntakeFieldNameMap = {
  gleason: 'Gleason',
  isupGrade: 'ISUP Grade',
  psa: 'PSA',
  psaDate: 'PSA date',
  psma: 'PSMA',
  positiveSite: 'Positive site',
  prostatectomy: 'Prostatectomy',
};

const lymphomaIntakeFieldNameMap = {
  stage: 'Stage',
  aOrBSymptoms: 'A/B Symptoms',
  extranodal: 'Extranodal',
  bulkDisease: 'Bulk Disease',
};

const headAndNeckIntakeFieldNameMap = {
  p16: 'P16',
  ajccV7: 'AJCC V7',
  smokingHistory: 'Smoking History',
  cigarettesPerDay: 'No. of cigarettes per day',
  yearsSmoked: 'Over how many year(s)',
  packYears: 'Pack years',
  dateOfSurgery: 'Date of surgery',
  extracapsularExtension: 'Extracapsular Extension (ECE)',
  margins: 'Margins',
  marginsMeasurement: 'Margins measurement (mm)',
  periNeuralInvasion: 'Peri Neural Invasion - Skin H&N',
  pniType: 'PNI Type',
  patientDiscussedAtMdt: 'Patient discussed at MDT',
  treatmentConsistentMdt: 'Treatment consistent with MDT recommendations',
};

const intakeTypeFieldNameMap = {
  carepathbaselinebreastSet: breastIntakeFieldNameMap,
  carepathbaselinelungSet: lungIntakeFieldNameMap,
  carepathbaselineprostateSet: urogenitalIntakeFieldNameMap,
  carepathbaselinelymphomaSet: lymphomaIntakeFieldNameMap,
  carepathbaselineheadandneckSet: headAndNeckIntakeFieldNameMap,
};

const EXCLUDED_LATERALITIES = ['Not Applicable', 'Unpaired'];

const IntakeDetails = ({ intakeData, intakeFieldNameMap }: { intakeData: any; intakeFieldNameMap: any }) => {
  const intakeFields = Object.entries(intakeFieldNameMap).filter(
    ([key]) => intakeData[key] != null && intakeData[key] !== '',
  );

  // Calculate maxElementsPerColumn based on total elements
  const maxElementsPerColumn = intakeFields.length > 18 ? Math.ceil(intakeFields.length / 2) : 9;
  const numColumns = Math.ceil(intakeFields.length / maxElementsPerColumn);

  // Split fields into columns
  const columns = [];

  for (let i = 0; i < numColumns; i++) {
    const startIndex = i * maxElementsPerColumn;
    const endIndex = startIndex + maxElementsPerColumn;
    columns.push(intakeFields.slice(startIndex, endIndex));
  }

  return (
    <Grid container spacing={2}>
      {columns.map((column, columnIndex) => (
        <Grid item xs={12} md={6} key={columnIndex}>
          <Stack direction="column" spacing={2}>
            {column.map(([key, value]) => (
              <Stack key={key} direction="column" sx={{ flex: 1 }}>
                <Typography variant="subtitle2" color="text.secondary">
                  {String(value)}
                </Typography>
                <Typography variant="body2" color="text.primary">
                  {Array.isArray(intakeData[key]) ? intakeData[key].join(', ') : intakeData[key]}
                </Typography>
              </Stack>
            ))}
          </Stack>
        </Grid>
      ))}
    </Grid>
  );
};

const getDiagnosisTitle = (diagnosis: any) => {
  return `${diagnosis?.diagnosisCode?.diagnosisCode} ${diagnosis?.diagnosisCode?.tumourStream?.name || ''}`;
};

const cleanupString = (str: string | null | undefined) => {
  if (!str) return '-';
  return str;
};

const getFullDiagnosisTitle = (diagnosis: any) => {
  return (
    `${cleanupString(diagnosis?.diagnosisCode?.diagnosisCode)} ${cleanupString(
      diagnosis?.diagnosisCode?.tumourStream?.name,
    )}: ${cleanupString(diagnosis?.diagnosisCode?.diagnosisDescription)}` +
    (diagnosis?.laterality && !EXCLUDED_LATERALITIES.includes(diagnosis?.laterality)
      ? ` (${diagnosis?.laterality})`
      : '')
  );
};
const getTNMStaging = (diagnosis: any) => {
  const tnmStaging = [
    diagnosis.stageTCodePathological?.subCode ? `p${diagnosis.stageTCodePathological?.subCode}` : '',
    diagnosis.stageNCodePathological?.subCode ? `p${diagnosis.stageNCodePathological?.subCode}` : '',
    diagnosis.stageMCodePathological?.subCode ? `p${diagnosis.stageMCodePathological?.subCode}` : '',
    diagnosis.stageTCodeClinical?.subCode ? `c${diagnosis.stageTCodeClinical?.subCode}` : '',
    diagnosis.stageNCodeClinical?.subCode ? `c${diagnosis.stageNCodeClinical?.subCode}` : '',
    diagnosis.stageMCodeClinical?.subCode ? `c${diagnosis.stageMCodeClinical?.subCode}` : '',
  ];
  return tnmStaging.filter(Boolean).join(' ') || '-';
};

const DiagnosisDetails = ({
  diagnosis,
  setActiveDiagnosisId,
}: {
  diagnosis: any;
  setActiveDiagnosisId: (diagnosisId: string) => void;
}) => {
  const theme = useTheme();
  const primaryDiagnosis = diagnosis.relatedPrimaryDiagnosis || diagnosis;
  const metastasisDiagnosis = !diagnosis.isPrimaryDiagnosis ? diagnosis : null;
  const intakeType = Object.keys(intakeTypeFieldNameMap).find(
    (key) => diagnosis?.[key] && Array.isArray(diagnosis[key]) && diagnosis[key].length > 0,
  );
  const intakeFieldNameMap = intakeType
    ? intakeTypeFieldNameMap[intakeType as keyof typeof intakeTypeFieldNameMap]
    : null;
  let intakeData = intakeType ? diagnosis[intakeType as keyof typeof intakeTypeFieldNameMap][0] : null;

  // Apply extra processing for prostate
  if (intakeType === 'carepathbaselineprostateSet') {
    const { gleasonOne, gleasonTwo, isupGrade, psaDate, ...rest } = intakeData;
    const gleason = `${gleasonOne || ''} + ${gleasonTwo || ''} = ${gleasonOne + gleasonTwo}`;
    intakeData = {
      ...rest,
      gleason: gleasonOne !== null && gleasonTwo !== null ? gleason : '',
      isupGrade: isupGrade ? `Grade ${isupGrade}` : '',
      psaDate: psaDate ? dayjs(psaDate).format(CurrentAppConfig.PatientCard.defaultAlternateDateFormat) : '',
    };
  }
  if (intakeType === 'carepathbaselineheadandneckSet') {
    const { stageTCode, stageNCode, stageMCode, dateOfSurgery, ...rest } = intakeData;
    const stageT = stageTCode ? `${stageTCode.split('-')[0]}` : '';
    const stageN = stageNCode ? `${stageNCode.split('-')[0]}` : '';
    const stageM = stageMCode ? `${stageMCode.split('-')[0]}` : '';
    const surgeryDate = dateOfSurgery
      ? dayjs(dateOfSurgery).format(CurrentAppConfig.PatientCard.defaultAlternateDateFormat)
      : '';
    intakeData = {
      ...rest,
      ajccV7: stageT || stageN || stageM ? `${stageT} ${stageN} ${stageM}` : '',
      dateOfSurgery: surgeryDate,
    };
  }
  // If the intake data is empty, we don't want to show the intake data card
  const isEmptyIntakeData =
    !intakeData ||
    Object.entries(intakeData)
      .filter(([key]) => !['id', '__typename'].includes(key))
      .every(([key, value]) => value === null || value === '');

  return (
    <Stack sx={{ height: '100%', flex: 1, minHeight: 0, display: 'flex', flexDirection: 'column' }}>
      <Stack direction="column" spacing={1} width="100%" padding="8px 16px">
        {metastasisDiagnosis && (
          <Stack direction="column" width="100%">
            <Typography variant="body2" color="text.primary">
              Metastasis Diagnosis
            </Typography>
            <Typography variant="body2" color="text.secondary">
              {getFullDiagnosisTitle(metastasisDiagnosis)}
            </Typography>
          </Stack>
        )}
        <Stack direction="column" width="100%">
          {metastasisDiagnosis ? (
            <Typography
              variant="body2"
              color={theme.palette.info.dark}
              sx={{ cursor: 'pointer', textDecoration: 'underline' }}
              onClick={() => {
                setActiveDiagnosisId(primaryDiagnosis.id);
              }}>
              Related Primary Diagnosis
            </Typography>
          ) : (
            <Typography variant="body2" color="text.primary">
              Primary Diagnosis
            </Typography>
          )}
          <Typography variant="body2" color="text.secondary" data-testid="diagnosisTitle">
            {getFullDiagnosisTitle(primaryDiagnosis)}
          </Typography>
        </Stack>
      </Stack>
      <Divider />
      <Stack
        direction="row"
        spacing={2}
        width="100%"
        height="100%"
        sx={{ overflow: 'hidden', flex: 1, minHeight: 0, display: 'flex' }}>
        <Stack direction="column" spacing={2} width="50%" height="100%" sx={{ overflow: 'hidden', padding: '16px' }}>
          <Typography variant="subtitle2" color="text.primary">
            Staging {primaryDiagnosis.ajccMajorVersion && `(AJCC ${primaryDiagnosis.ajccMajorVersion}th Edition)`}
          </Typography>
          {diagnosis.isPrimaryDiagnosis && (
            <>
              <Stack>
                <Typography variant="subtitle2" color="text.secondary">
                  Morphology
                </Typography>
                <Typography variant="body2" color="text.primary">
                  {primaryDiagnosis.morphology
                    ? `${primaryDiagnosis.morphology.code} ${primaryDiagnosis.morphology.description}`
                    : '-'}
                </Typography>
              </Stack>
              <Stack>
                <Typography variant="subtitle2" color="text.secondary">
                  Staging
                </Typography>
                <Typography variant="body2" color="text.primary">
                  {getTNMStaging(primaryDiagnosis)}
                </Typography>
              </Stack>
            </>
          )}
          <Stack>
            <Typography variant="subtitle2" color="text.secondary">
              Diagnosis date
            </Typography>
            <Typography variant="body2" color="text.primary">
              {diagnosis.diagnosisDate
                ? dayjs(diagnosis.diagnosisDate).format(CurrentAppConfig.PatientCard.defaultAlternateDateFormat)
                : '-'}
            </Typography>
          </Stack>
          <Stack>
            <Typography variant="subtitle2" color="text.secondary">
              Diagnosed by
            </Typography>
            <Typography variant="body2" color="text.primary">
              {diagnosis.practitioner?.firstName && diagnosis.practitioner?.lastName
                ? `${diagnosis.practitioner?.prefix} ${diagnosis.practitioner?.firstName} ${diagnosis.practitioner?.lastName}`
                : '-'}
            </Typography>
          </Stack>
        </Stack>
        {intakeFieldNameMap && intakeData && !isEmptyIntakeData && (
          <>
            <Divider orientation="vertical" flexItem />
            <Stack
              direction="column"
              width="50%"
              sx={{
                overflowY: 'auto',
                flex: 1,
                minHeight: 0,
                display: 'flex',
                gap: 2,
                paddingTop: '16px',
              }}>
              <Typography variant="subtitle2" color="text.primary">
                Intake
              </Typography>
              <Stack
                direction="column"
                width="100%"
                sx={{ overflowY: 'auto', flex: 1, minHeight: 0, display: 'flex', gap: 2 }}>
                <IntakeDetails intakeData={intakeData} intakeFieldNameMap={intakeFieldNameMap} />
              </Stack>
            </Stack>
          </>
        )}
      </Stack>
    </Stack>
  );
};

const DiagnosisCard = ({ name, patientDiagnosis }: Props): JSX.Element => {
  const { NO_DIAGNOSIS } = CONSTANTS;
  const diagnosisId = patientDiagnosis?.length ? patientDiagnosis[0].id : 0;
  const [selectedDiagnosisId, setSelectedDiagnosisId] = useState<string | number>(diagnosisId);
  const theme = useTheme();
  return (
    <div
      data-testid="diagnosisCard"
      style={{
        height: '100%',
        minHeight: 0,
        minWidth: 0,
        width: '100%',
        display: 'flex',
        flexDirection: 'column',
        flex: 1,
        overflow: 'hidden',
      }}>
      <CardTitle>
        {name}{' '}
        {patientDiagnosis?.length > 0 && (
          <Badge $bgColor={theme.palette.primary.main} data-testid="diagnosisBadge">
            {patientDiagnosis.length}
          </Badge>
        )}
      </CardTitle>
      <Box
        sx={{
          height: '100%',
          minHeight: 0,
          minWidth: 0,
          width: '100%',
          flex: 1,
          display: 'flex',
          flexDirection: 'column',
        }}>
        {patientDiagnosis.length > 0 ? (
          <TabContext value={selectedDiagnosisId}>
            {patientDiagnosis.length > 1 && (
              <TabList
                onChange={(event, newValue) => setSelectedDiagnosisId(newValue)}
                variant="scrollable"
                scrollButtons="auto"
                sx={{
                  minWidth: 0,
                  maxWidth: '100%',
                  '& .MuiTabs-scroller': {
                    maxWidth: '100%',
                  },
                }}>
                {patientDiagnosis.map((diagnosis: any, index: number) => (
                  <Tab key={diagnosis.id} label={getDiagnosisTitle(diagnosis)} value={diagnosis.id} />
                ))}
              </TabList>
            )}
            {patientDiagnosis.map((diagnosis: any, index: number) => (
              <TabPanel
                key={diagnosis.id}
                value={diagnosis.id}
                sx={{
                  p: 0,
                  minHeight: 0,
                  minWidth: 0,
                  maxWidth: '100%',
                  height: '100%',
                }}>
                <DiagnosisDetails diagnosis={diagnosis} setActiveDiagnosisId={setSelectedDiagnosisId} />
              </TabPanel>
            ))}
          </TabContext>
        ) : (
          <CardBodyGroup>{NO_DIAGNOSIS}</CardBodyGroup>
        )}
      </Box>
    </div>
  );
};

export default DiagnosisCard;
